import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Add,
  AutoAwesomeRounded,
  ChevronRightSharp,
  ClearRounded,
  ReorderRounded,
  SaveOutlined,
} from '@mui/icons-material';
import { AccordionDetails, Badge, Box, Tooltip, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useMemo, useState } from 'react';

import { useFetchData } from '../../../../hooks/useFetchData';
import { deleteCategory, reorderQuestions, updateCategory } from '../../../../services/dealGPT';
import {
  StyledAccordion,
  StyledAccordionSummary,
  StyledIconButton,
} from '../../../../theme/CustomComponents';
import { Question, QuestionCategory } from '../../../../Types/dealGPT';
import { categoryQuestionsPathUrl } from '../../../../util/routesUrls';
import { AnalyticsEvent, useAnalytics } from '../../../Providers/AnalyticsProvider';
import { useAuth } from '../../../Providers/AuthProvider';
import { useFeatures } from '../../../Providers/FeatureProvider';
import ConfirmationDialog from '../ConfirmationDialog';
import EditableDialog from '../EditableDialog';
import CategoryContextMenu from './CategoryContextMenu';
import CategoryForm from './CategoryForm';
import QuestionLibraryDefaultPrompt from './DefaultPrompt';
import QuestionsTable from './QuestionsTable';
import QuestionsTableSkeleton from './QuestionsTableSkeleton';
import { useReorderableList } from './useReorderableList';

interface CategoryAccordionProps {
  editable: boolean;
  category: QuestionCategory;
  reorderingCategories: boolean;
  defaultExpanded?: boolean;
  refetchCategories: () => void;
}

const CategoryAccordion: React.FC<CategoryAccordionProps> = ({
  editable,
  category,
  reorderingCategories,
  defaultExpanded,
  refetchCategories,
}) => {
  const { currentUser, isAdmin } = useAuth();
  const analytics = useAnalytics();
  const features = useFeatures();
  const [editingCategory, setEditingCategory] = useState<boolean>(false);
  const [showEditPromptDialog, setShowEditPromptDialog] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [expanded, setExpanded] = useState<boolean>(defaultExpanded ?? false);
  const [inserting, setInserting] = useState<boolean>(false);
  const [reorderingQuestions, setReorderingQuestions] = useState<boolean>(false);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: category.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: 'grab',
  };

  const {
    data: questions,
    loading: loadingQuestions,
    refetch: refetchQuestions,
  } = useFetchData<Question[]>({
    urlPath: categoryQuestionsPathUrl(category.id),
    skip: !expanded,
  });

  const {
    orderedItems: orderedQuestions,
    setOrderedItems: setOrderedQuestions,
    handleDragEnd,
  } = useReorderableList<Question>(questions ?? []);

  const handleAccordionChange = useCallback(
    (_: React.SyntheticEvent, isExpanded: boolean) => {
      // Prevent accordion from expanding if reordering categories
      if (reorderingCategories) return;

      setExpanded(isExpanded);
      if (!isExpanded) {
        setInserting(false);
      }
    },
    [reorderingCategories]
  );

  const onNewQuestion = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      setInserting(true);
      if (expanded) e.stopPropagation();
    },
    [expanded]
  );

  const onDeleteCategory = useCallback(async () => {
    try {
      analytics.event(AnalyticsEvent.WM_IA_QL_DELETE_CATEGORY);

      await deleteCategory(category.id);
      refetchCategories();
    } catch {
      enqueueSnackbar('Failed to delete category.', { variant: 'error' });
    } finally {
      setShowDeleteConfirmation(false);
    }
  }, [category, refetchCategories, analytics]);

  const saveCategory = useCallback(
    async (name: string) => {
      try {
        analytics.event(AnalyticsEvent.WM_IA_QL_EDIT_CATEGORY);

        await updateCategory({
          id: category.id,
          name,
        });
        setEditingCategory(false);
        refetchCategories();
      } catch {
        enqueueSnackbar('Failed to save category.', { variant: 'error' });
      }
    },
    [category.id, refetchCategories, analytics]
  );

  const saveCategoryPrompt = useCallback(
    async (newPrompt: string) => {
      try {
        analytics.event(AnalyticsEvent.WM_IA_QL_CATEGORY_CUSTOM_PROMPT);

        await updateCategory({
          id: category.id,
          customPrompt: newPrompt,
        });
        setShowEditPromptDialog(false);
        refetchCategories();
      } catch {
        enqueueSnackbar('Failed to save category prompt.', { variant: 'error' });
      }
    },
    [category.id, refetchCategories, analytics]
  );

  const saveQuestionsOrder = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      try {
        e.stopPropagation();
        await reorderQuestions(orderedQuestions);
        setReorderingQuestions(false);
        refetchQuestions();
      } catch {
        enqueueSnackbar('Failed to reorder questions.', { variant: 'error' });
      }
    },
    [orderedQuestions, refetchQuestions]
  );

  const resetQuestionsOrder = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      setOrderedQuestions(questions ?? []);
      setReorderingQuestions(false);
    },
    [questions, setOrderedQuestions]
  );

  const questionCount = useMemo(
    () => questions?.length ?? category.questionCount,
    [questions, category]
  );

  return (
    <StyledAccordion
      expanded={expanded}
      onChange={handleAccordionChange}
      ref={setNodeRef}
      style={style}
    >
      <StyledAccordionSummary expandIcon={<ChevronRightSharp />}>
        {editingCategory ? (
          <CategoryForm
            currentName={category.name}
            onSave={saveCategory}
            onCancel={() => setEditingCategory(false)}
          />
        ) : (
          <Box sx={{ display: 'flex', flexGrow: 1, justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="subtitle2">{category.name}</Typography>
                <Typography sx={{ fontStyle: 'italic' }}>
                  {questionCount
                    ? `${questionCount} question${questionCount > 1 ? 's' : ''}`
                    : 'No questions'}
                </Typography>
              </Box>
              {editable && !reorderingCategories && !reorderingQuestions && (
                <Tooltip title="New Question" arrow>
                  <StyledIconButton onClick={onNewQuestion}>
                    <Add />
                  </StyledIconButton>
                </Tooltip>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {reorderingQuestions && (
                <>
                  <Tooltip title="Click to save changes" arrow>
                    <StyledIconButton onClick={saveQuestionsOrder}>
                      <SaveOutlined />
                    </StyledIconButton>
                  </Tooltip>
                  <Tooltip title="Cancel" arrow>
                    <StyledIconButton onClick={resetQuestionsOrder}>
                      <ClearRounded />
                    </StyledIconButton>
                  </Tooltip>
                </>
              )}
              {editable &&
                !reorderingQuestions &&
                !reorderingCategories &&
                (isAdmin || features.ql_custom_prompt) && (
                  <Tooltip
                    title={
                      category.customPrompt
                        ? 'This category uses a custom prompt, which will be applied to all questions that do not have their own custom prompt'
                        : 'Define a custom prompt for this category'
                    }
                    arrow
                  >
                    <StyledIconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowEditPromptDialog(true);
                      }}
                    >
                      <Badge
                        badgeContent={category.customPrompt ? '!' : undefined}
                        color="primary"
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        overlap="circular"
                      >
                        <AutoAwesomeRounded />
                      </Badge>
                    </StyledIconButton>
                  </Tooltip>
                )}
              {editable && !reorderingQuestions && !reorderingCategories && (
                <CategoryContextMenu
                  setExpanded={setExpanded}
                  setReordering={setReorderingQuestions}
                  setEditingCategory={setEditingCategory}
                  setShowDeleteConfirmation={setShowDeleteConfirmation}
                />
              )}
              {reorderingCategories && (
                <Tooltip title="Drag to reorder this question" arrow>
                  <StyledIconButton {...attributes} {...listeners} sx={{ cursor: 'grab' }}>
                    <ReorderRounded />
                  </StyledIconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        )}
      </StyledAccordionSummary>
      <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', padding: 4, gap: 2 }}>
        {questions && questions.length == 0 && (
          <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
            No questions found. Create a new question to get started.
          </Typography>
        )}
        {loadingQuestions ? (
          <QuestionsTableSkeleton hasActions={editable} />
        ) : (
          <QuestionsTable
            editable={editable}
            reordering={reorderingQuestions}
            questions={
              inserting
                ? [
                    {
                      id: 'new',
                      categoryId: category.id,
                      question: '',
                      sortOrder: 0,
                      createdAt: new Date(),
                      createdBy: currentUser?.id ?? '',
                    },
                    ...orderedQuestions,
                  ]
                : orderedQuestions
            }
            handleDragEnd={handleDragEnd}
            refetchQuestions={refetchQuestions}
            onNewQuestionSaved={() => setInserting(false)}
          />
        )}

        {showDeleteConfirmation && (
          <ConfirmationDialog
            title="Delete Category?"
            message="Are you sure you want to delete this category? This action cannot be undone."
            close={() => setShowDeleteConfirmation(false)}
            confirm={onDeleteCategory}
          />
        )}
        {showEditPromptDialog && (
          <EditableDialog
            title="Edit Category Prompt"
            message="You're about to replace the default prompt. Make sure your custom prompt is well-formatted so the system can correctly append the user's question at the end of the prompt"
            initialValue={category.customPrompt || QuestionLibraryDefaultPrompt}
            close={() => setShowEditPromptDialog(false)}
            save={saveCategoryPrompt}
          />
        )}
      </AccordionDetails>
    </StyledAccordion>
  );
};

export default CategoryAccordion;
